<template>
    <div class="group flex items-stretch" :class="wrapperClassName">
        <div class="text-gray-500 flex items-stretch" v-if="contentBefore">
            <slot name="before">
                <span class="self-center ml-3" v-text="before" />
            </slot>
        </div>
        <div class="relative grow">
            <input
                :id="id"
                v-bind="$attrs"
                class="form-input"
                :class="internalInputClassName"
                :disabled="disabled"
                :readonly="readonly"
                :type="type"
                :value="parsedValue"
                ref="input"
                v-on="inputListeners"
                v-mask="maskOptions"
            >
        </div>
        <div class="text-gray-500 flex items-stretch" v-if="contentAfter">
            <div class="self-center relative h-4 w-4 mr-3" v-tooltip="errorMessage" v-if="error">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-50" />
                <AlertCircle class="h-4 w-4 text-red-500" />
            </div>
            <Spinner class="self-center h-4 w-4 mr-3" v-else-if="loading" />
            <slot name="after" v-else>
                <span class="self-center mr-3" v-text="after" />
            </slot>
        </div>
    </div>
</template>

<script>
    import insertTextAtCursor from 'insert-text-at-cursor';
    import { IMaskDirective, IMask } from 'vue-imask';
    import AlertCircle from '../../assets/svg/bold/alert-circle.svg';
    import { locale } from '../../locale/index.js';
    import Spinner from '../Spinner/Spinner.vue';

    /**
     * Input allows to insert short text values.
     * @displayName Input
     */
    export default {
        name: 'ActivixInput',

        components: {
            Spinner,
            AlertCircle,
        },

        directives: {
            mask: IMaskDirective,
        },

        inheritAttrs: false,

        props: {
            allowNegativeValue: {
                default: false,
                type: Boolean,
            },
            after: {
                default: '',
                type: String,
            },
            before: {
                default: '',
                type: String,
            },
            disabled: {
                default: false,
                type: Boolean,
            },
            error: {
                default: false,
                type: Boolean,
            },
            errorMessage: {
                default: '',
                type: String,
            },
            highlighted: {
                default: false,
                type: Boolean,
            },
            boldHighlighted: {
                default: false,
                type: Boolean,
            },
            id: {
                default: '',
                type: String,
            },
            inputClassName: {
                default: '',
                type: null,
            },
            invisible: {
                default: false,
                type: Boolean,
            },
            loading: {
                default: false,
                type: Boolean,
            },
            mask: {
                default: '',
                type: [String, Array],
            },
            maskMoneySymbol: {
                default: '$',
                type: String,
            },
            maskRadix: {
                default: '',
                type: String,
            },
            maskScale: {
                default: null,
                type: Number,
            },
            maskThousandsSeparator: {
                default: '',
                type: String,
            },
            maskUpperCase: {
                default: false,
                type: Boolean,
            },
            min: {
                default: -2147483647, // PostgreSQL minimum
                type: Number,
                validator: value => typeof value === 'number' && +value <= 2147483647 && +value >= -2147483647,
            },
            max: {
                default: 2147483647, // PostgreSQL maximum
                type: Number,
                validator: value => typeof value === 'number' && +value <= 2147483647 && +value >= -2147483647,
            },
            paddingClassName: {
                default: 'py-2 px-3',
                type: null,
            },
            readonly: {
                default: false,
                type: Boolean,
            },
            type: {
                default: 'text',
                type: String,
            },
            value: {
                default: '',
                type: null,
                validator: value => typeof value === 'string' || typeof value === 'number' || value === null,
            },
        },

        data() {
            return {
                isFocused: false,
            };
        },

        computed: {
            isFloat() {
                return ['decimal', 'money', 'percent'].includes(this.mask);
            },

            isInteger() {
                return ['integer', 'km', 'mi'].includes(this.mask);
            },

            parsedValue() {
                if (!this.value) {
                    return '';
                }

                return this.value.toString();
            },

            locale() {
                return locale.value;
            },

            contentBefore() {
                return this.before || this.$slots.before;
            },

            contentAfter() {
                return this.after || this.error || this.loading || this.$slots.after;
            },

            maskOptions() {
                if (!this.mask) {
                    return null;
                }

                switch (this.mask) {
                    case 'integer':
                        return {
                            ...this.maskNumericOptions,
                            scale: 0,
                        };

                    case 'decimal':
                        return this.maskNumericOptions;

                    case 'money':
                        return {
                            eager: true,
                            mask: this.locale === 'fr'
                                ? `num ${this.maskMoneySymbol}`
                                : `${this.maskMoneySymbol}num`,
                            blocks: {
                                num: {
                                    ...this.maskNumericOptions,
                                    scale: 2,
                                },
                            },
                        };

                    case 'percent':
                        return {
                            eager: true,
                            mask: 'num %',
                            blocks: {
                                num: {
                                    ...this.maskNumericOptions,
                                    max: 100,
                                },
                            },
                        };

                    case 'km':
                    case 'mi':
                        return {
                            eager: true,
                            mask: `num ${this.mask}`,
                            blocks: {
                                num: {
                                    ...this.maskNumericOptions,
                                    scale: 0,
                                },
                            },
                        };

                    default:
                        return {
                            lazy: true,
                            mask: this.maskPattern,
                            prepare: value => {
                                return this.maskUpperCase || this.mask === 'postalCode'
                                    ? value.toLocaleUpperCase()
                                    : value;
                            },
                            definitions: {
                                '#': /[1-9]/,
                            },
                        };
                }
            },

            maskPattern() {
                switch (this.mask) {
                    case 'phone':
                        return '#00 000-0000';
                    case 'phoneWithCountry':
                        return '{+1} #00 000-0000';
                    case 'postalCode':
                        return 'a0a 0a0';
                    case 'zipCode':
                        return '00000[-0000]';
                    default:
                        return this.mask;
                }
            },

            maskNumericOptions() {
                const defaultRadix = this.locale === 'fr' ? ',' : '.';
                const defaultThousandsSeparator = this.locale === 'fr' ? ' ' : ',';

                return {
                    lazy: true,
                    mask: Number,
                    signed: true,
                    max: this.max,
                    min: this.allowNegativeValue ? this.min : 0,
                    scale: this.maskScale || 2,
                    radix: this.maskRadix || defaultRadix,
                    mapToRadix: ['.', ','],
                    thousandsSeparator: this.maskThousandsSeparator || defaultThousandsSeparator,
                };
            },

            wrapperClassName() {
                const hasValue = this.parsedValue.length > 0;

                const focusClassName = this.error
                    ? 'ring-1 ring-red-500 border-red-500'
                    : 'ring-1 ring-blue-500 border-blue-500';

                return [
                    'border rounded overflow-hidden',
                    'transition-colors duration-150',
                    this.isFocused ? focusClassName : 'border-gray-300',
                    {
                        'border-opacity-0': this.invisible && !this.isFocused,
                        'hover:border-opacity-100 focus-within:border-opacity-100': !this.disabled,
                        'border-b-blue-500 ': !this.disabled && !this.invisible && this.highlighted && hasValue,
                        'bg-blue-500': !this.disabled && !this.invisible && this.boldHighlighted && hasValue,
                    },
                ];
            },

            internalInputClassName() {
                const hasValue = this.parsedValue.length > 0;

                return [
                    'block min-w-0 w-full border-0 rounded placeholder-gray-800 placeholder-opacity-30',
                    'focus:outline-none focus:ring-0 focus:border-transparent',
                    'transition-colors duration-150',
                    this.paddingClassName,
                    this.inputClassName,
                    {
                        'sm:text-sm': !this.$ui.oldFontScale,
                        'bg-transparent': this.invisible,
                        'bg-gray-200': this.disabled && !this.invisible,
                        'cursor-not-allowed': this.disabled,
                        'bg-blue-500 text-white': !this.disabled && !this.invisible && this.boldHighlighted && hasValue,
                    },
                ];
            },

            inputListeners() {
                return {
                    ...this.$listeners,
                    accept: this.onAccept,
                    blur: this.onBlur,
                    focus: this.onFocus,
                    input: this.onInput,
                };
            },
        },

        watch: {
            async locale() {
                if (!this.parsedValue || !this.mask) {
                    return;
                }

                const value = this.parsedValue;

                await this.$nextTick();

                const masked = IMask.createMask(this.maskOptions);
                masked.resolve(value);

                this.$refs.input.value = masked.value;

                this.update(value);
            },
        },

        methods: {
            async focus() {
                await this.$nextTick();

                if (typeof this.$refs.input.focus !== 'undefined') {
                    this.$refs.input.focus();
                } else {
                    this.$refs.input.$el.focus();
                }
            },

            blur() {
                if (typeof this.$refs.input.blur !== 'undefined') {
                    this.$refs.input.blur();
                } else {
                    this.$refs.input.$el.blur();
                }
            },

            async insertAtCaret(text) {
                await this.$nextTick();

                insertTextAtCursor(this.$refs.input, text);
            },

            updateNumericValue(event) {
                if (!this.isFloat && !this.isInteger) {
                    return;
                }

                const masked = IMask.createMask(this.maskOptions);
                masked.resolve(event.target.value);

                if (event.data === '0' || masked.unmaskedValue !== '0') {
                    return;
                }

                masked.resolve(event.data);

                if (masked.unmaskedValue) {
                    this.$refs.input.value = masked.value;
                }
            },

            onFocus() {
                this.isFocused = true;
                this.$emit('focus');
            },

            onBlur() {
                this.isFocused = false;
                this.$emit('blur');
            },

            onInput(event) {
                this.updateNumericValue(event);

                if (!this.mask) {
                    this.update(event.target.value);
                }
            },

            onAccept(event) {
                if (this.mask) {
                    this.update(event.detail.unmaskedValue);
                }
            },

            update(value) {
                if (!value) {
                    this.$emit('input', value);

                    return;
                }

                let formattedValue;

                if (this.isFloat) {
                    formattedValue = parseFloat(value);
                } else if (this.isInteger) {
                    formattedValue = parseInt(value, 10);
                } else {
                    formattedValue = value;
                }

                if (!formattedValue) {
                    return;
                }

                this.$emit('input', formattedValue);
            },
        },
    };
</script>
